import {useMemo, useState} from "react";
import {Spinner, Typography} from "@bolteu/clay-react";
import {useAppDispatch} from "webform/store/hooks";

import {useI18n} from "i18n/i18n";

import {deleteAttachmentAction, removeFailedAttachment} from "webform/slices/attachmentSlice";
import {formatBytes} from "webform/utils/formatBytes";
import {textSplitter} from "webform/utils/textSplitter";

import config from "webform/config/config";
import {ErrorType} from "config/errors";

import alertIcon from "webform/assets/alert.svg";
import closeIcon from "webform/assets/close-icon.svg";

import * as Styled from "./attachment.styled";

interface AttachmentsProps {
    attachmentId?: number;
    file: File;
    error?: ErrorType;
    loading?: boolean;
}

export const Attachment = ({file, error, loading, attachmentId}: AttachmentsProps) => {
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    const fileType = file.type.split("/")[1] ?? file.type;
    const dispatch = useAppDispatch();
    const i18n = useI18n();

    const fileSubtitle = textSplitter(formatBytes(file.size), fileType, "・");

    const [showDelete, setShowDelete] = useState(isTouch);

    const handleDelete = () => {
        if (attachmentId) dispatch(deleteAttachmentAction({attachment_id: attachmentId}));
        else dispatch(removeFailedAttachment(file));
    };

    const onMouseEnter = () => {
        setShowDelete(true);
    };

    const onMouseLeave = () => {
        setShowDelete(false);
    };

    const subtitle = useMemo(() => {
        if (!error) return fileSubtitle;
        switch (error) {
            case ErrorType.FILE_TOO_BIG:
                return i18n("error.file_too_big", {values: {file_size_limit: config.fileSizeLimitInMb}});
            case ErrorType.WRONG_FILE_FORMAT:
                return i18n("error.file_not_supported");
            default:
                return i18n("error.generic.title");
        }
    }, [error, i18n, fileSubtitle]);

    return (
        <Styled.AttachmentContainer onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <Styled.TextColumn>
                <Styled.FileName>
                    <Typography className={`bolt-font-body-m-regular${loading ? " text-secondary" : ""}`}>
                        {file.name}
                    </Typography>
                </Styled.FileName>
                <Styled.FileSubtitle $error={!!error}>
                    <Typography className={`bolt-font-body-s-regular${error ? "" : " text-secondary"}`}>
                        {subtitle}
                    </Typography>
                </Styled.FileSubtitle>
            </Styled.TextColumn>
            <Styled.IconColumn>
                {!showDelete && loading && <Spinner size="xs" />}
                {!showDelete && error && <img src={alertIcon} alt="error icon" />}
                {showDelete && (
                    <Styled.DeleteButton onClick={handleDelete}>
                        <img src={closeIcon} alt="delete button" />
                    </Styled.DeleteButton>
                )}
            </Styled.IconColumn>
        </Styled.AttachmentContainer>
    );
};
